import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { api } from '../api.js'
import { useAuth } from '../auth/AuthContext.jsx'
import { useOrg } from '../org/OrgContext.jsx'

// Admin: monthly attendance billing, run by hand.
//
// The same job runs unattended on the 1st (cron). This page is the manual
// path: it opens on a DRY RUN — everything that would be invoiced right now,
// broken down per course — and the button then runs it for real.
//
// Presences from DEACTIVATED courses are gathered too (they're flagged in the
// table): a course that ended last month still has sessions to bill.
// Re-running is safe — only presences that were never invoiced are picked up.

export function AdminBillingPage() {
  const { t } = useTranslation()
  const { org } = useOrg()
  const { user: me, loading: authLoading } = useAuth()

  const [preview, setPreview] = useState(null)
  const [done, setDone] = useState(null)   // summary of the real run
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  function reload() {
    setLoading(true)
    setError(null)
    api.admin.billing.preview()
      .then((d) => setPreview(d))
      .catch((e) => setError(e.code || 'server'))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    if (authLoading || !me || me.role !== 'admin') return
    reload()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, me])

  async function run() {
    if (!confirm(t('admin_billing.confirm_run', {
      amount: preview?.amount ?? 0, currency: org.currency_label,
    }))) return
    setBusy(true); setError(null); setDone(null)
    try {
      const d = await api.admin.billing.run()
      setDone(d)
      reload()
    } catch (e) {
      setError(e.code || 'server')
    } finally { setBusy(false) }
  }

  if (authLoading) return <main className="container"><p>{t('common.loading')}</p></main>
  if (!me || me.role !== 'admin') {
    return <main className="container"><p className="error">{t('admin.errors.not_admin')}</p></main>
  }

  const courses = preview?.courses || []
  const nothing = !loading && (preview?.presences ?? 0) === 0

  return (
    <main className="container">
      <p><Link to="/admin">← {t('admin.title')}</Link></p>
      <h1>{t('admin_billing.title')}</h1>
      <p className="muted">{t('admin_billing.intro')}</p>
      <p className="muted small">{t('admin_billing.note_deactivated')}</p>

      {error && (
        <p className="error">
          {t(`admin_billing.errors.${error}`, {
            defaultValue: t(`admin.errors.${error}`, { defaultValue: error }),
          })}
        </p>
      )}

      {done && (
        <p className="success">
          {/* Counts go through pluralized fragments (RO needs one/few/other,
              including the "de" from 20 up) — the sentence just joins them. */}
          {t('admin_billing.done', {
            payments: t('admin_billing.n_invoices', { count: done.payments }),
            presences: t('admin_billing.n_presences_billed', { count: done.presences }),
            amount: done.amount,
            currency: org.currency_label,
          })}
          {(done.payments_settled ?? 0) > 0 && (
            <> {t('admin_billing.done_settled', { count: done.payments_settled })}</>
          )}
          {(done.prepaid_presences ?? 0) > 0 && (
            <> {t('admin_billing.done_prepaid', { count: done.prepaid_presences })}</>
          )}
        </p>
      )}

      {loading && <p>{t('common.loading')}</p>}

      {!loading && (
        <>
          <section className="card">
            <h2>{t('admin_billing.preview_title')}</h2>
            {nothing
              ? <p className="muted">{t('admin_billing.nothing')}</p>
              : (
                <p>
                  {t('admin_billing.summary', {
                    users: t('admin_billing.n_users', { count: preview.users_billed }),
                    presences: t('admin_billing.n_presences', { count: preview.presences }),
                    courses: t('admin_billing.n_courses', { count: courses.length }),
                  })}
                  {' '}
                  <strong>{preview.amount} {org.currency_label}</strong>
                  {(preview.discount ?? 0) > 0 && (
                    <>
                      {' '}
                      <span className="muted">
                        ({t('admin_billing.discount_total', {
                          amount: preview.discount, currency: org.currency_label,
                        })})
                      </span>
                    </>
                  )}
                </p>
              )}

            {(preview?.prepaid_presences ?? 0) > 0 && (
              <p className="muted small">
                {t('admin_billing.prepaid_note', { count: preview.prepaid_presences })}
              </p>
            )}

            {courses.length > 0 && (
              <table className="results-table">
                <thead>
                  <tr>
                    <th>{t('admin_billing.col_course')}</th>
                    <th>{t('admin_billing.col_type')}</th>
                    <th>{t('admin_billing.col_participants')}</th>
                    <th>{t('admin_billing.col_presences')}</th>
                    <th>{t('admin_billing.col_prepaid')}</th>
                    <th>{t('admin_billing.col_discount')}</th>
                    <th>{t('admin_billing.col_amount')}</th>
                  </tr>
                </thead>
                <tbody>
                  {courses.map((c) => (
                    <tr key={c.course_id}>
                      <td>
                        <Link to={`/admin/courses/${c.course_id}/edit`}>{c.course_name}</Link>
                        {Number(c.active) !== 1 && (
                          <span className="badge muted-badge" style={{ marginLeft: '0.4rem' }}>
                            {t('admin_billing.inactive')}
                          </span>
                        )}
                      </td>
                      <td>{t(`admin_courses.type_${c.type}`, { defaultValue: c.type })}</td>
                      <td>{c.participants}</td>
                      <td>{c.presences}</td>
                      <td>
                        {(c.prepaid ?? 0) > 0
                          ? c.prepaid
                          : <span className="muted">&mdash;</span>}
                      </td>
                      <td>
                        {(c.discount ?? 0) > 0
                          ? <>−{c.discount} {org.currency_label}</>
                          : <span className="muted">—</span>}
                      </td>
                      <td>{c.amount} {org.currency_label}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            <div className="form-actions">
              <button type="button" disabled={busy || nothing} onClick={run}>
                {busy ? t('common.loading') : t('admin_billing.run')}
              </button>
              <button type="button" className="link-button" disabled={busy} onClick={reload}
                      style={{ marginLeft: '0.75rem' }}>
                {t('admin_billing.refresh')}
              </button>
            </div>
          </section>
        </>
      )}
    </main>
  )
}
